import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { UserUpdateError } from '../../../../../../corelogic/domain/errors/user.error';
import { store } from '../../../../../../corelogic/state/store';
import {
  setUserError,
  updateUserProfile,
} from '../../../../../../corelogic/state/user/user.action';
import { UPDATE_USER_PROFILE_SUCCESS } from '../../../../../../corelogic/state/user/user.action.type';
import { CountrySelector } from '../../Shared/CountrySelector';
import { SuccessModal } from '../../Shared/SuccessModal/SuccessModal';

interface NationalityEditorProps {
  user: any;
  token: string;
  onSuccess?: () => void;
  isUserLoading?: boolean;
}

export const NationalityEditor: React.FC<NationalityEditorProps> = ({
  user,
  token,
  onSuccess,
  isUserLoading = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [nationality, setNationality] = useState(user?.nationality || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pendingNationalityUpdate, setPendingNationalityUpdate] =
    useState(false);

  // Track if there have been changes
  const [hasChanges, setHasChanges] = useState(false);

  // Update nationality when user data changes
  useEffect(() => {
    if (user) {
      setNationality(user.nationality || '');
    }
  }, [user]);

  // Listen for successful change nationality to show success modal
  useEffect(() => {
    const subscription = store.action$.subscribe((action) => {
      if (
        action.type === UPDATE_USER_PROFILE_SUCCESS &&
        pendingNationalityUpdate
      ) {
        handleCloseEditor();
        setShowSuccessModal(true);
        setPendingNationalityUpdate(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [pendingNationalityUpdate]);

  // Update hasChanges when nationality changes
  useEffect(() => {
    if (isEditing) {
      setHasChanges(nationality !== user?.nationality);
    }
  }, [nationality, user?.nationality, isEditing]);

  const handleOpenEditor = () => {
    setNationality(user?.nationality || '');
    setHasChanges(false);
    setIsEditing(true);
  };

  const handleCloseEditor = () => {
    setIsEditing(false);
  };

  const handleNationalityChange = (code: string) => {
    setNationality(code);
    setHasChanges(code !== user?.nationality);
  };

  const handleSubmit = async () => {
    if (!token) {
      store.dispatch(
        setUserError('You must be logged in to update your nationality'),
      );
      return;
    }

    // If nationality hasn't changed, just close
    if (!hasChanges) {
      handleCloseEditor();
      return;
    }

    setIsSubmitting(true);
    setPendingNationalityUpdate(true);

    try {
      await store.dispatch(
        updateUserProfile({
          token,
          currentProfile: user,
          newData: { nationality },
          retry: 0,
        }),
      );
    } catch (error) {
      if (error instanceof UserUpdateError) {
        setPendingNationalityUpdate(false);
        store.dispatch(setUserError(error.message));
      } else {
        store.dispatch(setUserError('Failed to update nationality'));
      }
      console.error('Failed to update nationality:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Edit Button */}
      <Tooltip title="Change Nationality">
        <IconButton
          size="small"
          sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 2 }}
          onClick={handleOpenEditor}
          disabled={isUserLoading}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Nationality Edit Dialog */}
      <Dialog
        open={isEditing}
        onClose={isSubmitting ? undefined : handleCloseEditor}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: 'white',
            minWidth: '450px',
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontFamily: 'Rubik Mono One',
            fontSize: '1.1rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pb: 1,
          }}
        >
          Change Nationality
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 1 }}>
          <CountrySelector
            value={nationality}
            onChange={handleNationalityChange}
            sx={{ mt: 1, width: '100%' }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={handleCloseEditor}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || !hasChanges}
            sx={{
              backgroundColor: '#8B0000',
              color: 'white',
              '&:hover': {
                backgroundColor: '#aa0000',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(139, 0, 0, 0.5)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Nationality Updated"
        message="Your nationality has been successfully updated."
        autoHideDuration={3000}
      />
    </>
  );
};
